"use client";

// Code-animated mockups for the four DivetIQ Featured Modules cards.
// Each mockup demonstrates the module visually using SVG/CSS-driven motion.
// Content is rendered from initial paint - animations are decorative only,
// and respect prefers-reduced-motion.

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { AnimatedCounter } from "@/components/motion/animated-counter";
import { CheckIcon } from "@/components/icons/icons";

const dotPattern =
  "radial-gradient(circle at 1px 1px, rgba(14,18,27,0.06) 1px, transparent 0)";

function MockupFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-[220px] overflow-hidden rounded-2xl border border-neutral-100 bg-greyscale-25/80 backdrop-blur-[1px] sm:h-[300px]">
      <div
        aria-hidden
        className="absolute inset-0 opacity-60"
        style={{ backgroundImage: dotPattern, backgroundSize: "12px 12px" }}
      />
      <div className="relative flex size-full items-center justify-center p-6">
        {children}
      </div>
    </div>
  );
}

/* 1. Procurement - animated supplier feed */

const SUPPLIERS = [
  { name: "ACME Steel GmbH", country: "DE", po: "PO-9821" },
  { name: "Lumière Logistique", country: "FR", po: "PO-9822" },
  { name: "Adriatic Components", country: "BA", po: "PO-9823" },
] as const;

const STATUSES = ["3-way matched", "PEPPOL sent", "OFAC cleared", "Risk: low"];

export function ContactMockup() {
  const reduce = useReducedMotion();
  const [activeIdx, setActiveIdx] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % SUPPLIERS.length);
      setStatusIdx((i) => (i + 1) % STATUSES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <MockupFrame>
      <div className="w-full max-w-[320px] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0_14px_28px_-12px_rgba(14,18,27,0.18)]">
        <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
            Suppliers · Live
          </span>
          <span className="flex h-1.5 w-1.5 items-center">
            <motion.span
              animate={reduce ? undefined : { opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              className="h-1.5 w-1.5 rounded-full bg-accent-green"
            />
          </span>
        </div>
        <ul className="divide-y divide-neutral-100">
          {SUPPLIERS.map((s, i) => (
            <li
              key={s.po}
              className={`flex items-center justify-between px-3 py-2.5 text-[12px] transition-colors ${
                i === activeIdx ? "bg-brand-soft/60" : ""
              }`}
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-semibold text-neutral-950">
                  {s.name}
                </span>
                <span className="text-[10px] text-neutral-400">
                  {s.country} · {s.po}
                </span>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                  i === activeIdx
                    ? "bg-brand text-white"
                    : "bg-neutral-100 text-neutral-500"
                }`}
              >
                {i === activeIdx ? STATUSES[statusIdx] : "idle"}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t border-neutral-100 px-3 py-2 text-[11px] text-neutral-500">
          <span>Sourcing event #312</span>
          <span className="font-mono text-neutral-700">€2.4M</span>
        </div>
      </div>
    </MockupFrame>
  );
}

/* 2. HCM - payroll automation flow */

const FLOW_STEPS = [
  { step: "Recruit", title: "ATS", accent: "bg-accent-aqua/15 text-accent-aqua" },
  { step: "Hire", title: "Offer + onboarding", accent: "bg-brand-soft text-brand" },
  { step: "Pay", title: "Multi-country payroll", accent: "bg-accent-purple/15 text-accent-purple" },
] as const;

export function AutomationMockup() {
  const reduce = useReducedMotion();
  return (
    <MockupFrame>
      <div className="relative w-full max-w-[320px] space-y-2">
        {FLOW_STEPS.map((f, i) => (
          <div key={f.step}>
            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-white p-2.5 shadow-[0_1px_2px_rgba(14,18,27,0.06)]">
              <div className="flex items-center gap-2.5">
                <span
                  className={`grid size-7 place-items-center rounded-md ${f.accent}`}
                >
                  <span className="text-[10px] font-bold">{i + 1}</span>
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                    {f.step}
                  </p>
                  <p className="text-[12px] font-medium text-neutral-950">
                    {f.title}
                  </p>
                </div>
              </div>
              <CheckIcon className="size-3.5 text-neutral-300" />
            </div>
            {i < FLOW_STEPS.length - 1 && (
              <div className="ml-[18px] flex flex-col items-center">
                <span className="block h-3 w-px bg-neutral-200" />
              </div>
            )}
          </div>
        ))}
        {!reduce && (
          <motion.span
            aria-hidden
            initial={{ y: 0, opacity: 0 }}
            animate={{
              y: [0, 50, 100, 50, 0],
              opacity: [0, 1, 1, 1, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute left-[14px] top-3 block size-2 rounded-full bg-brand shadow-[0_0_0_4px_rgba(62,111,243,0.18)]"
          />
        )}
      </div>
    </MockupFrame>
  );
}

/* 3. ERP - Order-to-Cash kanban */

type Deal = { id: string; amount: string };
const O2C_COLS: { label: string; tone: string }[] = [
  { label: "Quote", tone: "bg-accent-aqua" },
  { label: "PO", tone: "bg-brand" },
  { label: "Paid", tone: "bg-accent-green" },
];
const INITIAL_DEALS: Deal[][] = [
  [
    { id: "d1", amount: "€18.5K" },
    { id: "d2", amount: "€12.8K" },
  ],
  [{ id: "d3", amount: "€25K" }],
  [{ id: "d4", amount: "€20K" }],
];

export function PipelineMockup() {
  const reduce = useReducedMotion();
  const nextDealId = useRef(5);
  const [columns, setColumns] = useState<Deal[][]>(INITIAL_DEALS);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setColumns((cols) => {
        const [c0, c1, c2] = cols.map((c) => c.slice());
        if (c1.length) c2.unshift(c1.shift()!);
        if (c0.length) c1.unshift(c0.shift()!);
        c0.unshift({
          id: `d${nextDealId.current++}`,
          amount: ["€8.2K", "€15K", "€22.4K", "€10K"][
            Math.floor(Math.random() * 4)
          ],
        });
        if (c2.length > 2) c2.pop();
        return [c0, c1, c2];
      });
    }, 2400);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <MockupFrame>
      <div className="grid w-full max-w-[340px] grid-cols-3 gap-2">
        {O2C_COLS.map((col, i) => (
          <div
            key={col.label}
            className="rounded-lg border border-neutral-100 bg-white p-2 shadow-[0_1px_2px_rgba(14,18,27,0.04)]"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                <span className={`size-1.5 rounded-full ${col.tone}`} />
                {col.label}
              </span>
              <span className="text-[10px] font-semibold text-neutral-400">
                {columns[i].length}
              </span>
            </div>
            <div className="space-y-1.5">
              {columns[i].map((d) => (
                <motion.div
                  key={d.id}
                  layout
                  initial={reduce ? false : { opacity: 0, y: -6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 240, damping: 22 }}
                  className="rounded-md border border-neutral-100 bg-white px-2 py-1.5"
                >
                  <p className="text-[10px] font-medium text-neutral-400">
                    Acme Co
                  </p>
                  <p className="text-[11px] font-semibold text-neutral-950">
                    {d.amount}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </MockupFrame>
  );
}

/* 4. BI - Working capital dashboard */

export function DashboardMockup() {
  const reduce = useReducedMotion();
  return (
    <MockupFrame>
      <div className="w-full max-w-[320px] rounded-xl border border-neutral-100 bg-white p-3 shadow-[0_1px_2px_rgba(14,18,27,0.06)]">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-semibold text-neutral-950">
            Working capital · €
          </p>
          <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[9px] font-medium text-neutral-500">
            Last 6 weeks
          </span>
        </div>
        <p className="text-[20px] font-semibold leading-none text-neutral-950">
          <AnimatedCounter prefix="€" value={4.61} suffix="M" decimals={2} />
        </p>

        <svg
          viewBox="0 0 240 96"
          className="mt-3 h-20 w-full"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="dash-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3e6ff3" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3e6ff3" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,72 L24,60 L48,64 L72,40 L96,48 L120,28 L144,32 L168,18 L192,24 L216,12 L240,16 L240,96 L0,96 Z"
            fill="url(#dash-area)"
          />
          <motion.path
            d="M0,72 L24,60 L48,64 L72,40 L96,48 L120,28 L144,32 L168,18 L192,24 L216,12 L240,16"
            fill="none"
            stroke="#3e6ff3"
            strokeWidth="1.8"
            initial={reduce ? false : { pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
          />
        </svg>

        <div className="mt-3 rounded-md border border-neutral-100 bg-greyscale-25 px-2.5 py-2">
          <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-brand-strong">
            Auto-narrative
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-neutral-700">
            EMEA dropped 22%, driven by Mid-Market Segment in Product Line X.
          </p>
        </div>
      </div>
    </MockupFrame>
  );
}
